import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { verifyAuth, logout } from '../services/api';
import { Button } from '../components/ui/button';
import { 
  ArrowLeft, LogOut, Users, Rocket, Handshake, Mic, ClipboardList, 
  RefreshCw, UserCircle, ChevronDown, ChevronRight, Globe, Database,
  Building2, Presentation
} from 'lucide-react';

const AdminLayout = ({ children, currentPage }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [authenticated, setAuthenticated] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    website: true,
    data: true,
    presentations: true
  });

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      await verifyAuth();
      setAuthenticated(true);
    } catch (error) {
      navigate('/admin');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/admin');
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 text-white flex items-center justify-center">
        <div className="text-lg">Loading...</div>
      </div>
    );
  }

  if (!authenticated) {
    return null;
  }

  // Grouped navigation structure
  const navSections = [
    {
      id: 'website',
      label: 'Website Content',
      icon: Globe,
      items: [
        { id: 'testimonials', label: 'Testimonials', icon: Users, path: '/admin/testimonials' },
        { id: 'decision-makers', label: 'Decision Makers', icon: UserCircle, path: '/admin/decision-makers' },
        { id: 'startups', label: 'List of Companies', icon: Building2, path: '/admin/startups' },
        { id: 'judges', label: 'Judges', icon: UserCircle, path: '/admin/judges' },
        { id: 'podcast', label: 'Podcast', icon: Mic, path: '/admin/podcast' },
        { id: 'sponsors', label: 'Sponsors', icon: Handshake, path: '/admin/sponsors' },
      ]
    },
    {
      id: 'data',
      label: 'Data Management',
      icon: Database,
      items: [
        { id: 'applications', label: 'Applications', icon: ClipboardList, path: '/admin/applications' },
        { id: 'airtable-sync', label: 'Airtable Sync', icon: RefreshCw, path: '/admin/airtable-sync' },
      ]
    },
    {
      id: 'presentations',
      label: 'Presentations',
      icon: Presentation,
      items: [
        { id: 'slideshows', label: 'Pitch Deck Slides', icon: Presentation, path: '/admin/slideshows' },
      ]
    }
  ];

  // Find current section for highlighting
  const getCurrentSection = () => {
    for (const section of navSections) {
      if (section.items.some(item => item.id === currentPage)) {
        return section.id;
      }
    }
    return null;
  };

  return (
    <div className="min-h-screen bg-slate-950 text-white flex">
      {/* Sidebar */}
      <div className="w-64 bg-slate-900 border-r border-slate-800 flex flex-col flex-shrink-0">
        {/* Logo/Header */}
        <div className="p-4 border-b border-slate-800">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-lg flex items-center justify-center">
              <Rocket className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-white">Admin</h1>
              <p className="text-xs text-slate-500">The Founder Initiative</p>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-3 overflow-y-auto">
          {navSections.map((section) => (
            <div key={section.id} className="mb-2">
              {/* Section Header */}
              <button
                onClick={() => toggleSection(section.id)}
                className="w-full flex items-center justify-between px-3 py-2 text-sm font-medium text-slate-400 hover:text-white rounded-lg hover:bg-slate-800/50 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <section.icon className="w-4 h-4" />
                  {section.label}
                </div>
                {expandedSections[section.id] ? (
                  <ChevronDown className="w-4 h-4" />
                ) : (
                  <ChevronRight className="w-4 h-4" />
                )}
              </button>

              {/* Section Items */}
              {expandedSections[section.id] && (
                <div className="ml-2 mt-1 space-y-1">
                  {section.items.map((item) => (
                    <button
                      key={item.id}
                      onClick={() => navigate(item.path)}
                      className={`w-full flex items-center gap-2 px-3 py-2 text-sm rounded-lg transition-colors ${
                        currentPage === item.id
                          ? 'bg-blue-600 text-white'
                          : 'text-slate-400 hover:text-white hover:bg-slate-800'
                      }`}
                    >
                      <item.icon className="w-4 h-4" />
                      {item.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}
        </nav>

        {/* Footer */}
        <div className="p-3 border-t border-slate-800">
          <div className="text-xs text-slate-500 mb-2 px-3 truncate">
            {localStorage.getItem('adminEmail')}
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/')}
              className="flex-1 border-slate-700 text-slate-400 hover:bg-slate-800 text-xs"
            >
              <ArrowLeft className="w-3 h-3 mr-1" />
              Site
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleLogout}
              className="flex-1 border-slate-700 text-slate-400 hover:bg-slate-800 text-xs"
            >
              <LogOut className="w-3 h-3 mr-1" />
              Logout
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-h-screen overflow-hidden">
        {/* Top bar */}
        <div className="h-14 border-b border-slate-800 bg-slate-900/50 flex items-center px-6 flex-shrink-0">
          <h2 className="text-lg font-semibold text-white capitalize">
            {currentPage?.replace('-', ' ') || 'Dashboard'}
          </h2>
        </div>

        {/* Content Area */}
        <div className="flex-1 overflow-y-auto p-6">
          {children}
        </div>
      </div>
    </div>
  );
};

export default AdminLayout;
